/**
 * Hook für Publishing Schedule
 * 
 * Mutation für das Erstellen und Verwalten von Schedules.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import {
  createSchedule,
  createContentBlockSchedule,
  createChannelPackageSchedule,
} from '@/lib/api/publishing-schedules'
import type {
  ScheduleEntityType,
  CreateScheduleRequest,
  CreateScheduleForEntityRequest,
  SchedulePreset,
  SchedulePresetOption,
} from '@/types/publishing'
import { 
  addDays, 
  startOfDay, 
  endOfDay, 
  nextSaturday, 
  nextSunday,
  setHours,
  setMinutes,
} from 'date-fns'

/**
 * Hook for creating a schedule
 */
export function useCreateSchedule(
  entityType: ScheduleEntityType,
  entityId: string,
  channelId?: string
) {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: async (data: CreateScheduleForEntityRequest) => {
      switch (entityType) {
        case 'content_block':
          return createContentBlockSchedule(entityId, data)
        case 'channel_package':
          if (!channelId) throw new Error('channelId required for channel_package')
          return createChannelPackageSchedule(channelId, entityId, data)
        default:
          // Use central API
          return createSchedule({
            entity_type: entityType,
            entity_id: entityId,
            ...data,
          } as CreateScheduleRequest)
      }
    },
    onSuccess: (data) => {
      if (data.success) {
        toast.success(t('toast.success.versionScheduled'))
        // Invalidate related queries
        queryClient.invalidateQueries({
          queryKey: ['schedule', 'active', entityType, entityId]
        })
        queryClient.invalidateQueries({
          queryKey: ['schedule', 'conflict', entityType, entityId]
        })
        queryClient.invalidateQueries({
          queryKey: ['schedules']
        })
        queryClient.invalidateQueries({
          queryKey: ['versions', entityType, entityId]
        })
      } else {
        toast.error(data.error || t('toast.error.scheduling'))
      }
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })
}

/**
 * Schedule presets with date range calculation
 */
export const SCHEDULE_PRESETS: SchedulePresetOption[] = [
  {
    id: 'tomorrow_all_day',
    label: 'Morgen ganztägig',
    description: '00:00 - 23:59 Uhr',
    getDateRange: () => {
      const tomorrow = addDays(new Date(), 1)
      return {
        start: startOfDay(tomorrow),
        end: endOfDay(tomorrow),
      }
    },
  },
  {
    id: 'next_weekend',
    label: 'Nächstes Wochenende',
    description: 'Samstag 00:00 - Sonntag 23:59',
    getDateRange: () => {
      const saturday = nextSaturday(new Date())
      const sunday = nextSunday(new Date())
      return {
        start: startOfDay(saturday),
        end: endOfDay(sunday),
      }
    },
  },
  {
    id: 'custom',
    label: 'Benutzerdefiniert',
    description: 'Eigenen Zeitraum wählen',
    getDateRange: () => {
      // Default: Today 18:00 - Today 23:59
      const now = new Date()
      const start = setMinutes(setHours(now, 18), 0)
      const end = endOfDay(now)
      return { start, end }
    },
  },
]

/**
 * Get date range for a preset
 */
export function getPresetDateRange(preset: SchedulePreset): { start: Date; end: Date } {
  const presetOption = SCHEDULE_PRESETS.find(p => p.id === preset)
  if (!presetOption) {
    throw new Error(`Unknown preset: ${preset}`)
  }
  return presetOption.getDateRange()
}

/**
 * Validate schedule date range
 */
export function validateScheduleDateRange(
  startDate: Date | null,
  endDate: Date | null
): { isValid: boolean; error?: string } {
  if (!startDate || !endDate) {
    return { isValid: false, error: 'Start- und Enddatum sind erforderlich' }
  }

  const now = new Date()
  
  if (startDate < now) {
    return { isValid: false, error: 'Startdatum muss in der Zukunft liegen' }
  }

  if (endDate <= startDate) {
    return { isValid: false, error: 'Enddatum muss nach Startdatum liegen' }
  }

  // Max duration: 90 days
  const maxDuration = 90 * 24 * 60 * 60 * 1000
  if (endDate.getTime() - startDate.getTime() > maxDuration) {
    return { isValid: false, error: 'Maximale Dauer: 90 Tage' }
  }

  return { isValid: true }
}

/**
 * Format date for display (Europe/Berlin timezone)
 */
export function formatScheduleDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  return d.toLocaleString('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Format relative time for display
 */
export function formatRelativeTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date
  const now = new Date()
  const diffMs = d.getTime() - now.getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  const diffHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))

  if (diffMs < 0) {
    // Past
    const absDays = Math.abs(diffDays)
    const absHours = Math.abs(diffHours)
    if (absDays > 0) return `vor ${absDays} Tag${absDays > 1 ? 'en' : ''}`
    if (absHours > 0) return `vor ${absHours} Stunde${absHours > 1 ? 'n' : ''}`
    return 'gerade eben'
  } else {
    // Future
    if (diffDays > 0) return `in ${diffDays} Tag${diffDays > 1 ? 'en' : ''}`
    if (diffHours > 0) return `in ${diffHours} Stunde${diffHours > 1 ? 'n' : ''}`
    return 'in Kürze'
  }
}


